const applyOperatorToSupabase = (column, operator, value) => {
  if (operator === "is empty") {
    return {
      method: "or",
      params: [`${column}.is.null`, `${column}.eq.`],
    };
  }
  if (operator === "is not empty") {
    return {
      method: "not.is.null",
      params: [column],
    };
  }
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const isNumeric = !isNaN(value) && !isNaN(parseFloat(value)) && isFinite(value);
  const isRelatedTableColumn = (col) => {
    const relatedTablePatterns = [
      "free",
      "paid",
      "engagement",
      "followers",
      "views",
      "likes",
      "comments",
      "reach",
      "impressions",
      "quality",
      "score",
      "cpm",
      "cpc",
      "ctr",
      "conversion",
      "roi",
      "cost",
      "brand",
      "content",
      "audience",
      "overall",
      "review",
      "internal",
      "history",
    ];
    return relatedTablePatterns.some((pattern) => col.includes(pattern));
  };

  if (isRelatedTableColumn(column)) {
    return null;
  }

  if (isNumeric && column !== "followers") {
    const numValue = parseFloat(value);
    switch (operator) {
      case "equals":
        return {
          method: "eq",
          params: [column, numValue],
        };
      case "does not equal":
        return {
          method: "neq",
          params: [column, numValue],
        };
      case "greater than":
        return {
          method: "gt",
          params: [column, numValue],
        };
      case "less than":
        return {
          method: "lt",
          params: [column, numValue],
        };
    }
  }

  switch (operator) {
    case "contains":
      return {
        method: "ilike",
        params: [column, `%${value}%`],
      };
    case "does not contain":
      return {
        method: "not.ilike",
        params: [column, `%${value}%`],
      };
    case "equals":
      return {
        method: "eq",
        params: [column, value],
      };
    case "does not equal":
      return {
        method: "neq",
        params: [column, value],
      };
    case "starts with":
      return {
        method: "ilike",
        params: [column, `${value}%`],
      };
    case "ends with":
      return {
        method: "ilike",
        params: [column, `%${value}`],
      };
    case "is any of": {
      const targetValues = String(value)
        .split(",")
        .map((t) => t.trim());
      return {
        method: "in",
        params: [column, targetValues],
      };
    }
    default:
      return null;
  }
};

export const applyFiltersToQuery = (query, filters, filtersLogic = "and") => {
  if (!filters || filters.length === 0) {
    return query;
  }

  const validFilters = filters.filter((filter) => {
    const { column, operator, value } = filter;
    if (!value && operator !== "is empty" && operator !== "is not empty") {
      return false;
    }
    const filterConfig = applyOperatorToSupabase(column, operator, value);
    return filterConfig !== null;
  });

  if (validFilters.length === 0) {
    return query;
  }

  if (filtersLogic === "or") {
    const orConditions = [];

    validFilters.forEach((filter) => {
      const { column, operator, value } = filter;
      const filterConfig = applyOperatorToSupabase(column, operator, value);
      if (!filterConfig) return;

      const { method, params } = filterConfig;

      if (method === "or" && params.length === 2) {
        orConditions.push(`${params[0]},${params[1]}`);
      } else if (method === "not.is.null" && params.length === 1) {
        orConditions.push(`${params[0]}.not.is.null`);
      } else if (params.length === 2) {
        const [filterColumn, filterValue] = params;

        if (["gt", "lt", "gte", "lte", "eq", "neq"].includes(method)) {
          orConditions.push(`${filterColumn}.${method}.${filterValue}`);
        } else if (method === "not") {
          orConditions.push(filterValue);
        } else if (method === "ilike") {
          orConditions.push(`${filterColumn}.ilike.${filterValue}`);
        } else if (method === "not.ilike") {
          orConditions.push(`${filterColumn}.not.ilike.${filterValue}`);
        } else if (method === "in") {
          orConditions.push(`${filterColumn}.in.(${filterValue.join(",")})`);
        }
      }
    });

    if (orConditions.length > 0) {
      query = query.or(orConditions.join(","));
    }
  } else {
    validFilters.forEach((filter) => {
      const { column, operator, value } = filter;
      const filterConfig = applyOperatorToSupabase(column, operator, value);
      if (!filterConfig) return;
      const { method, params } = filterConfig;

      if (method === "or" && params.length === 2) {
        query = query.or(`${params[0]},${params[1]}`);
      } else if (method === "not.is.null" && params.length === 1) {
        query = query.not(params[0], "is", null);
      } else if (params.length === 2) {
        const [filterColumn, filterValue] = params;

        if (["gt", "lt", "gte", "lte", "eq", "neq"].includes(method)) {
          query = query[method](filterColumn, filterValue);
        } else if (method === "not") {
          query = query.not(filterValue);
        } else if (method === "ilike") {
          query = query.ilike(filterColumn, filterValue);
        } else if (method === "not.ilike") {
          query = query.not(filterColumn, "ilike", filterValue);
        } else if (method === "in") {
          query = query.in(filterColumn, filterValue);
        } else {
          console.warn(`Unknown method: ${method} for column: ${filterColumn}`);
        }
      }
    });
  }

  return query;
};

export default applyOperatorToSupabase;
